import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";

function Carteira() {
  const { user } = useContext(AuthContext);
  const [saldo, setSaldo] = useState(null);
  const [saldoBloqueado, setSaldoBloqueado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [valorSaque, setValorSaque] = useState("");
  const [chavePix, setChavePix] = useState("");
  const [tipoChavePix, setTipoChavePix] = useState("EMAIL");
  const [cpf, setCpf] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [transacoes, setTransacoes] = useState([]);
  const [saqueEmAndamento, setSaqueEmAndamento] = useState(false);

  const fetchSaldo = async () => {
    if (!user) {
      setErro("Usuário não autenticado");
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        setErro("Sessão expirada. Faça login novamente.");
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/carteira`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP ${response.status}`);
      }

      const data = await response.json();

      setSaldo(data.saldoDisponivel);
      setSaldoBloqueado(data.saldoBloqueado);
      setTransacoes(data.transacoes || []);

      const saqueConfirmado = data.transacoes?.some(
        (tx) => tx.tipo === "saque" && tx.status === "saque_confirmado"
      );

      if (saqueConfirmado) {
        setMensagem("✅ Seu saque foi confirmado e será creditado em até 48h via Pix.");
      }
    } catch (err) {
      console.error("❌ Erro ao buscar saldo:", err);
      setErro("Erro ao carregar saldo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSaldo();
  }, [user]);

  const handleSubmitSaque = async (e) => {
    e.preventDefault();
    setMensagem("");

    if (!valorSaque || !chavePix || !tipoChavePix || !cpf) {
      setMensagem("Preencha todos os campos.");
      return;
    }

    setSaqueEmAndamento(true);

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/carteira/saque/solicitar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          valor: parseFloat(valorSaque),
          chavePix,
          tipoChavePix,
          cpf,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMensagem("Saque solicitado com sucesso!");
        setShowForm(false);
        setValorSaque("");
        setChavePix("");
        setTipoChavePix("EMAIL");
        setCpf("");
        await fetchSaldo();
      } else {
        setMensagem(data.error || "Erro ao solicitar saque.");
      }
    } catch (error) {
      console.error("Erro ao solicitar saque:", error);
      setMensagem("Erro ao solicitar saque.");
    } finally {
      setSaqueEmAndamento(false);
    }
  };

  if (loading) return <p>Carregando saldo...</p>;
  if (erro) return <p className="text-red-500">{erro}</p>;

  return (
    <div className="max-w-md mx-auto bg-white shadow p-6 rounded text-center mt-8">
      <h2 className="text-2xl font-bold mb-4">Minha Carteira</h2>

      {mensagem && (
        <div className="mb-4 bg-green-100 text-green-800 p-2 rounded border border-green-400">
          {mensagem}
        </div>
      )}

      <p>Saldo disponível: <strong>R$ {saldo?.toFixed(2) || "0.00"}</strong></p>
      <p className="text-sm text-gray-600 mt-1">
        Saldo em processamento: R$ {saldoBloqueado?.toFixed(2) || "0.00"}
      </p>

      <button
        className={`mt-4 px-4 py-2 rounded text-white ${
          saldo >= 1 ? "bg-green-600 hover:bg-green-800" : "bg-gray-400 cursor-not-allowed"
        }`}
        onClick={() => setShowForm(true)}
        disabled={saldo < 1}
      >
        Solicitar Saque
      </button>

      {showForm && (
        <form onSubmit={handleSubmitSaque} className="mt-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Dados para saque</h3>

          <label className="block mb-1">Valor (mínimo R$20,00)</label>
          <input
            type="number"
            step="0.01"
            value={valorSaque}
            onChange={(e) => setValorSaque(e.target.value)}
            className="w-full p-2 border rounded mb-2"
          />

          <label className="block mb-1">Chave Pix</label>
          <input
            type="text"
            value={chavePix}
            onChange={(e) => setChavePix(e.target.value)}
            className="w-full p-2 border rounded mb-2"
          />

          <label className="block mb-1">Tipo de chave</label>
          <select
            value={tipoChavePix}
            onChange={(e) => setTipoChavePix(e.target.value)}
            className="w-full p-2 border rounded mb-2"
          >
            <option value="EMAIL">Email</option>
            <option value="CPF">CPF</option>
            <option value="PHONE">Celular</option>
          </select>

          <label className="block mb-1">CPF do titular</label>
          <input
            type="text"
            value={cpf}
            onChange={(e) => setCpf(e.target.value)}
            className="w-full p-2 border rounded mb-2"
          />

          <div className="flex gap-2 mt-4">
            <button
              type="submit"
              disabled={saqueEmAndamento}
              className={`${
                saqueEmAndamento
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-800"
              } text-white px-4 py-2 rounded`}
            >
              {saqueEmAndamento ? "Enviando..." : "Enviar Solicitação"}
            </button>
          </div>
        </form>
      )}

      <p className="mt-4 text-sm text-gray-600">Valor mínimo para saque: R$20,00</p>

      {transacoes.length > 0 && (
        <div className="mt-6 text-left">
          <h3 className="text-lg font-semibold mb-2">Histórico de Transações</h3>
          <table className="w-full border-collapse border border-gray-300 text-sm">
            <thead className="bg-gray-200">
              <tr>
                <th className="border border-gray-300 p-2 text-left">Data</th>
                <th className="border border-gray-300 p-2 text-left">Tipo</th>
                <th className="border border-gray-300 p-2 text-left">Valor</th>
                <th className="border border-gray-300 p-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {transacoes.map((tx) => (
                <tr key={tx.id} className="hover:bg-gray-100">
                  <td className="border border-gray-300 p-2">{tx.data}</td>
                  <td className="border border-gray-300 p-2 capitalize">{tx.tipo}</td>
                  <td className="border border-gray-300 p-2">R$ {tx.valor.toFixed(2)}</td>
                  <td
                    className={`border border-gray-300 p-2 ${
                      tx.status === "saque_confirmado"
                        ? "text-green-600"
                        : tx.status === "saque_rejeitado"
                        ? "text-red-600"
                        : "text-yellow-600"
                    }`}
                  >
                    {tx.status === "saque_confirmado"
                      ? "confirmado – valor será depositado em até 48h"
                      : tx.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Carteira;
